import React, { useState, useEffect, useCallback, startTransition } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '../config/apiBase';
import './ReportPage.css';
import './MonthlyReportsPage.css';

async function downloadMonthlyPdf(pdfUrl, monthYm) {
  if (!pdfUrl) return;
  const safeName = `doh-chodshi-${monthYm}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');
  try {
    const res = await fetch(pdfUrl, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = safeName;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');
  }
}

function apiNetworkErrorMessage(err) {
  const name = err && err.name;
  const msg = String((err && err.message) || err || '');
  if (name === 'TypeError' || /Failed to fetch|NetworkError|Load failed|network error/i.test(msg)) {
    return 'לא ניתן להתחבר לשרת ה-API. הריצו בשורת פקודה נפרדת: מתוך תיקיית landing-page/server הריצו npm start (פורט 3001) בזמן ש־npm start של הפרונט (landing-page) רץ על פורט 3000.';
  }
  return msg || 'שגיאת רשת';
}

async function fetchMonthlyReportsList(id, sec) {
  let res;
  try {
    res = await fetch(`${getApiBaseUrl()}/api/ad-monthly-reports/list`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adId: id, secret: sec }),
    });
  } catch (e) {
    return { ok: false, error: apiNetworkErrorMessage(e) };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data.error || data.details || `HTTP ${res.status}` };
  }
  return {
    ok: true,
    months: Array.isArray(data.months) ? data.months : [],
    meta: {
      firstImpressionDate: data.firstImpressionDate || null,
      lastCompleteMonth: data.lastCompleteMonth || null,
      business_phone: data.business_phone || '',
    },
  };
}

async function fetchGenerateMonth(id, sec, month) {
  let res;
  try {
    res = await fetch(`${getApiBaseUrl()}/api/ad-monthly-reports/generate-month`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ adId: id, secret: sec, month }),
    });
  } catch (e) {
    return { ok: false, error: apiNetworkErrorMessage(e) };
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    return { ok: false, error: data.error || data.details || `HTTP ${res.status}` };
  }
  return {
    ok: true,
    pdfUrl: data.pdfUrl || null,
    generatedAt: data.generatedAt || null,
  };
}

function MonthlyReportsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paramAdId = searchParams.get('adId') || '';
  const paramSecret = searchParams.get('secret') || '';

  const [adId, setAdId] = useState(paramAdId);
  const [secret, setSecret] = useState(paramSecret);
  const [months, setMonths] = useState([]);
  const [meta, setMeta] = useState({
    firstImpressionDate: null,
    lastCompleteMonth: null,
    business_phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  /** Per-month UI: 'generating' | 'error' while creating PDFs that were missing (see auto pipeline). */
  const [monthGenStatus, setMonthGenStatus] = useState({});

  useEffect(() => {
    setAdId(paramAdId);
    setSecret(paramSecret);
  }, [paramAdId, paramSecret]);

  const loadListOnly = useCallback(async (id, sec) => {
    const idT = String(id || '').trim();
    const secT = String(sec || '').trim();
    if (!idT || !secT) return { ok: false };

    setLoading(true);
    setError(null);
    setMonthGenStatus({});
    try {
      const result = await fetchMonthlyReportsList(idT, secT);
      if (!result.ok) {
        throw new Error(result.error || 'טעינה נכשלה');
      }
      setMonths(result.months);
      setMeta(result.meta);
      return { ok: true, months: result.months };
    } catch (e) {
      setError(e.message || 'טעינה נכשלה');
      setMonths([]);
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const runListAndAutoGenerateMissing = useCallback(async (idIn, secIn, signal) => {
    const idT = String(idIn || '').trim();
    const secT = String(secIn || '').trim();
    if (!idT || !secT) return;

    const aborted = () => Boolean(signal && signal.aborted);

    setLoading(true);
    setError(null);
    setMonthGenStatus({});
    try {
      const result = await fetchMonthlyReportsList(idT, secT);
      if (aborted()) return;
      if (!result.ok) {
        setError(result.error || 'טעינה נכשלה');
        setMonths([]);
        return;
      }
      setMonths(result.months);
      setMeta(result.meta);
      setLoading(false);

      const missing = result.months.filter((r) => !r.hasPdf);
      if (missing.length > 0) {
        setMonthGenStatus(Object.fromEntries(missing.map((m) => [m.month, 'generating'])));
      }
      for (const row of missing) {
        if (aborted()) break;
        const gen = await fetchGenerateMonth(idT, secT, row.month);
        if (aborted()) break;
        if (!gen.ok) {
          setMonthGenStatus((s) => ({ ...s, [row.month]: 'error' }));
          setError(gen.error || 'יצירת דוח נכשלה');
          continue;
        }
        setMonths((prev) =>
          prev.map((r) =>
            r.month === row.month
              ? {
                  ...r,
                  hasPdf: true,
                  pdfUrl: gen.pdfUrl,
                  generatedAt: gen.generatedAt,
                }
              : r
          )
        );
        setMonthGenStatus((s) => {
          const next = { ...s };
          delete next[row.month];
          return next;
        });
      }
    } catch (e) {
      if (!aborted()) {
        setError(e.message || 'טעינה נכשלה');
        setMonths([]);
      }
    } finally {
      if (!aborted()) setLoading(false);
    }
  }, []);

  /** Avoid Chrome "click handler took N ms" warnings — start async/state work after the event ticks. */
  const deferMonthlyPipeline = useCallback(
    (idIn, secIn, signal) => {
      queueMicrotask(() => {
        startTransition(() => {
          void runListAndAutoGenerateMissing(idIn, secIn, signal);
        });
      });
    },
    [runListAndAutoGenerateMissing]
  );

  useEffect(() => {
    const id = String(paramAdId || '').trim();
    const sec = String(paramSecret || '').trim();
    if (!id || !sec) return undefined;

    const ac = new AbortController();
    runListAndAutoGenerateMissing(id, sec, ac.signal);
    return () => ac.abort();
  }, [paramAdId, paramSecret, runListAndAutoGenerateMissing]);

  const handleGenerateAll = () => {
    const id = adId.trim();
    const sec = secret.trim();
    if (!id || !sec) return;

    queueMicrotask(() => {
      startTransition(() => {
        void (async () => {
          setGenerating(true);
          setError(null);
          try {
            let res;
            try {
              res = await fetch(`${getApiBaseUrl()}/api/ad-monthly-reports/generate-all`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ adId: id, secret: sec }),
              });
            } catch (e) {
              throw new Error(apiNetworkErrorMessage(e));
            }
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
              throw new Error(data.error || data.details || `HTTP ${res.status}`);
            }
            await loadListOnly(id, sec);
          } catch (e) {
            setError(e.message || 'יצירת דוחות נכשלה');
          } finally {
            setGenerating(false);
          }
        })();
      });
    });
  };

  const handleDelete = async (month) => {
    const id = adId.trim();
    const sec = secret.trim();
    if (!window.confirm(`למחוק את הדוח לחודש ${month}?`)) return;

    setError(null);
    try {
      let res;
      try {
        res = await fetch(`${getApiBaseUrl()}/api/ad-monthly-reports/delete`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ adId: id, secret: sec, month }),
        });
      } catch (e) {
        throw new Error(apiNetworkErrorMessage(e));
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.details || `HTTP ${res.status}`);
      }
      deferMonthlyPipeline(id, sec, null);
    } catch (e) {
      setError(e.message || 'מחיקה נכשלה');
    }
  };

  const handleSubmitCredentials = (e) => {
    e.preventDefault();
    const id = adId.trim();
    const sec = secret.trim();
    navigate(`/monthly-reports?adId=${encodeURIComponent(id)}&secret=${encodeURIComponent(sec)}`, {
      replace: true,
    });
  };

  return (
    <div className="report-page monthly-reports-page">
      <div className="report-hero">
        <h1>דוחות חודשיים (PDF)</h1>
        <p>יצירה והורדה לפי חודשים מתחילת ההצגות ב-Analytics</p>
      </div>

      <form className="monthly-reports-form report-form" onSubmit={handleSubmitCredentials}>
        <div className="form-row">
          <label htmlFor="mr-adId">מזהה פרסומת</label>
          <input
            id="mr-adId"
            value={adId}
            onChange={(e) => setAdId(e.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="form-row">
          <label htmlFor="mr-secret">סוד</label>
          <input
            id="mr-secret"
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            autoComplete="off"
          />
        </div>
        <button type="submit" disabled={loading}>
          טען רשימה
        </button>
      </form>

      {error && (
        <p className="muted" style={{ color: '#ff6b6b', marginBottom: 16 }}>
          {error}
        </p>
      )}

      {meta.firstImpressionDate && (
        <p className="monthly-reports-meta">
          הצגה ראשונה: {meta.firstImpressionDate} · עד חודש מלא אחרון:{' '}
          {meta.lastCompleteMonth || '—'}
        </p>
      )}

      <div className="monthly-reports-actions">
        <button type="button" onClick={handleGenerateAll} disabled={generating || !adId.trim() || !secret.trim()}>
          {generating ? 'מייצר…' : 'ייצר דוחות'}
        </button>
        <button
          type="button"
          className="secondary"
          onClick={() => deferMonthlyPipeline(adId, secret, null)}
          disabled={loading || !adId.trim() || !secret.trim()}
        >
          רענן
        </button>
      </div>

      {loading && <p className="muted">טוען…</p>}

      {!loading && months.length === 0 && adId.trim() && secret.trim() && !error && (
        <p className="muted">
          {meta.firstImpressionDate
            ? 'לא נמצאו חודשים בטווח (או אין נתונים).'
            : 'לא נמצאו הצגות לפרסומת — לא ניתן לבנות טווח חודשי.'}
        </p>
      )}

      {months.length > 0 && (
        <div className="monthly-reports-table-wrap">
          <table className="monthly-reports-table">
            <thead>
              <tr>
                <th>חודש</th>
                <th>סטטוס</th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {months.map((row) => (
                <tr key={row.month}>
                  <td>{row.month}</td>
                  <td>
                    {monthGenStatus[row.month] === 'generating' ? (
                      <span>דוח ביצירה…</span>
                    ) : monthGenStatus[row.month] === 'error' ? (
                      <span style={{ color: '#ff6b6b' }}>שגיאה ביצירה</span>
                    ) : row.hasPdf ? (
                      <span>
                        דוח נוצר
                        {row.generatedAt ? ` (${row.generatedAt.slice(0, 10)})` : ''}
                      </span>
                    ) : (
                      <span className="muted">אין דוח</span>
                    )}
                  </td>
                  <td>
                    <div className="monthly-reports-row-actions">
                      <button
                        type="button"
                        disabled={!row.pdfUrl}
                        onClick={() => row.pdfUrl && window.open(row.pdfUrl, '_blank', 'noopener,noreferrer')}
                      >
                        הצג דוח
                      </button>
                      <button
                        type="button"
                        disabled={!row.pdfUrl}
                        onClick={() => row.pdfUrl && downloadMonthlyPdf(row.pdfUrl, row.month)}
                      >
                        הורדת דוח
                      </button>
                      <button type="button" className="danger" onClick={() => handleDelete(row.month)}>
                        מחק דוח
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MonthlyReportsPage;
